// useDialogStore.js - Reusable confirm/alert dialog store
// Text content is sourced from window.PXUTheme.theme_settings with hardcoded fallbacks.
// Usage: await dialogStore.confirm('add_imprint_location') → returns true/false

import { defineStore } from 'pinia';

// Default dialog configs used as fallback when theme settings are not defined.
// Theme setting keys follow the pattern: dialog__{key}__{field}
// Example key: dialog__add_imprint_location__title
const DIALOG_DEFAULTS = {
  add_imprint_location: {
    title: 'Please Note',
    message: 'Adding more than one imprint location will incur additional fees to your total. You can review the updated breakdown before adding to the cart.',
    subMessage: 'Do you wish to continue?',
    confirmText: 'Continue',
    cancelText: 'Cancel'
  }
};

/**
 * Build dialog config by merging theme settings over hardcoded defaults.
 * @param {string} dialogKey
 * @returns {{ title, message, subMessage, confirmText, cancelText }}
 */
function resolveConfig(dialogKey) {
  const defaults = DIALOG_DEFAULTS[dialogKey] || {
    title: 'Please Note',
    message: '',
    subMessage: '',
    confirmText: 'Continue',
    cancelText: 'Cancel'
  };

  const ts = window.PXUTheme?.theme_settings || {};
  const p  = `dialog__${dialogKey}__`;

  return {
    title:       ts[`${p}title`]       || defaults.title,
    message:     ts[`${p}message`]     || defaults.message,
    subMessage:  ts[`${p}sub_message`] || defaults.subMessage,
    confirmText: ts[`${p}confirm`]     || defaults.confirmText,
    cancelText:  ts[`${p}cancel`]      || defaults.cancelText
  };
}

export const useDialogStore = defineStore('dialog', {
  state: () => ({
    visible:     false,
    title:       '',
    message:     '',
    subMessage:  '',
    confirmText: 'Continue',
    cancelText:  'Cancel',
    // Internal resolver — set when a dialog is opened
    _resolve: null
  }),

  actions: {
    /**
     * Open a confirm dialog and return a Promise<boolean>.
     * true  = user clicked Confirm
     * false = user clicked Cancel or closed the dialog
     *
     * @param {string} dialogKey - matches a key in DIALOG_DEFAULTS
     * @returns {Promise<boolean>}
     */
    confirm(dialogKey) {
      const cfg = resolveConfig(dialogKey);

      this.title       = cfg.title;
      this.message     = cfg.message;
      this.subMessage  = cfg.subMessage;
      this.confirmText = cfg.confirmText;
      this.cancelText  = cfg.cancelText;
      this.visible     = true;

      return new Promise((resolve) => {
        this._resolve = resolve;
      });
    },

    /** User clicked the confirm button */
    onConfirm() {
      this.visible = false;
      if (this._resolve) {
        this._resolve(true);
        this._resolve = null;
      }
    },

    /** User clicked cancel or pressed Escape */
    onCancel() {
      this.visible = false;
      if (this._resolve) {
        this._resolve(false);
        this._resolve = null;
      }
    }
  }
});
